import React, { Fragment, useState } from "react";
import { Card, Avatar, Badge, Spinner, Button } from "components/ui";
import clsx from "clsx";
import { formatBytes } from "utils/formatBytes";
import { colorFromText } from "utils/colorFromText";
import {
  getFileIcon,
  getInitials,
  formatDate,
  getVerificationStatusInfo,
} from "../utils/documentUtils";
import { DocumentActions } from "./DocumentActions";
import { getUsersAssignedToDocVerification } from "services/leadsService";
import { toast } from "sonner";
import { downloadLeadDocument } from "services/leadsService";
import { getDocumentUrl } from "../utils/documentUtils";
import { ViewDocumentModal } from "./ViewDocumentModal";


const DocumentCard = ({
  document,
  refetch,
  refreshProgressJourney = () => { },
  onVerificationAction,
  IsFinalApproved = false,
  isStudent = false,
}) => {
  const { Icon, colorClass } = getFileIcon(document.fileType);

  const uploaderName = (() => {
    if (typeof document?.uploadedBy === "string" && document.uploadedBy.trim().length > 0) {
      return document.uploadedBy.trim();
    }
    if (typeof document?.updatedBy === "string" && document.updatedBy.trim().length > 0) {
      return document.updatedBy.trim();
    }
    if (typeof document?.actorName === "string" && document.actorName.trim().length > 0) {
      return document.actorName.trim();
    }
    return "Unknown";
  })();

  const initials = getInitials(uploaderName);
  const avatarColor = colorFromText(uploaderName);
  const { label, color } = getVerificationStatusInfo(
    document.verificationStatus,
  );

  // Safe formatting functions
  const safeFormatBytes = (bytes) => {
    try {
      if (typeof bytes !== "number" || isNaN(bytes) || bytes < 0) {
        return "Unknown size";
      }
      return formatBytes(bytes);
    } catch (error) {
      console.warn("Error formatting bytes:", error);
      return "Unknown size";
    }
  };

  const safeFormatDate = (dateString) => {
    try {
      if (!dateString) return "N/A";
      return formatDate(dateString);
    } catch (error) {
      console.warn("Error formatting date:", error);
      return "N/A";
    }
  };


  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const handleViewDocument = () => {
    setIsViewModalOpen(true);
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const result = await downloadLeadDocument(doc.id, doc.name);
      if (result?.success) toast.success("Document downloaded successfully.");
    } catch (error) {
      toast.error("Error downloading document.");
    }
  };

  const handleOpenInNewTab = (doc) => {
    window.open(getDocumentUrl(doc?.filePath), "_blank");
  };

  return (
    <>
      <Card
        className="p-3 sm:p-4 cursor-pointer transition-all duration-200 hover:shadow-md border-gray-200 dark:border-dark-600"
        onClick={handleViewDocument}
      >
        <div className="flex items-start space-x-3">
          <div className={clsx("flex-shrink-0", colorClass)}>
            <Icon className="h-6 w-6 sm:h-8 sm:w-8" />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <h3 className="dark:text-dark-50 truncate text-sm leading-tight font-medium text-gray-900">
                  {document.name}
                </h3>
                <p className="dark:text-dark-400 mt-0.5 text-xs text-gray-500">
                  {safeFormatBytes(document.size)}
                </p>
              </div>
              <div onClick={(e) => e.stopPropagation()}>
                <DocumentActions
                  document={document}
                  refetch={refetch}
                  useCase="card"
                  IsFinalApproved={IsFinalApproved}
                  refreshProgressJourney={refreshProgressJourney}
                  onVerificationAction={onVerificationAction}
                  onView={handleViewDocument}
                  isStudent={isStudent}
                />
              </div>
            </div>

            <div className="mt-3 flex items-center space-x-2">
              <Avatar
                size={8}
                color={avatarColor}
              >
                {initials}
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="dark:text-dark-300 text-xs leading-tight text-gray-600">
                  {uploaderName}
                </p>
                <p className="dark:text-dark-400 text-xs text-gray-500">
                  {safeFormatDate(document.uploadedAt)}
                </p>
              </div>
            </div>

            <div className="mt-3 space-y-2">
              <Badge variant="soft" color={color} className="text-[11px]">
                {label}
              </Badge>
            </div>
          </div>
        </div>
      </Card>

      <ViewDocumentModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        document={document}
        onDownload={handleDownloadDocument}
        onOpenInNewTab={handleOpenInNewTab}
        onVerificationAction={onVerificationAction}
        isStudent={isStudent}
      />
    </>
  );
};

export default DocumentCard;
